import AuthService from './authService';
import generateId from '../utils/generateId';

class PreferenceLearningService {
  static getCurrentUser() {
    return AuthService.getCurrentUser() || { id: 'guest', name: 'Guest' };
  }

  // Store "not interested" feedback
  static recordNotInterestedFeedback(feedback) {
    const currentUser = this.getCurrentUser();
    const allFeedback = localStorage.getItem('notInterestedFeedback');
    const feedbackData = allFeedback ? JSON.parse(allFeedback) : {};
    
    if (!feedbackData[currentUser.id]) {
      feedbackData[currentUser.id] = [];
    }
    
    const feedbackEntry = {
      ...feedback,
      userId: currentUser.id,
      id: generateId('feedback')
    };
    
    feedbackData[currentUser.id].push(feedbackEntry);
    localStorage.setItem('notInterestedFeedback', JSON.stringify(feedbackData));
    
    // Also update user preferences based on feedback
    this.updateUserPreferences(feedback);
    
    return { success: true, message: 'Thank you for the feedback! We\'ll improve your recommendations.' };
  }

  // Update user preferences based on feedback patterns
  static updateUserPreferences(feedback) {
    const currentUser = this.getCurrentUser();
    const preferencesKey = `userPreferences_${currentUser.id}`;
    const existingPrefs = localStorage.getItem(preferencesKey);
    const preferences = existingPrefs ? JSON.parse(existingPrefs) : {
      dislikedCategories: {},
      dislikedPriceRanges: {},
      dislikedKeywords: [],
      preferredPriceRange: null,
      locationPreferences: {},
      generalDislikes: []
    };

    const { responses } = feedback;

    // Update category preferences
    if (responses.categoryFeedback === 'hate' || responses.categoryFeedback === 'dislike') {
      const category = feedback.activityCategory;
      if (category) {
        preferences.dislikedCategories[category] = (preferences.dislikedCategories[category] || 0) + 1;
      }
    }

    // Update price range preferences
    if (responses.priceFeedback) {
      const priceRange = feedback.activityPriceRange;
      if (responses.priceFeedback === 'too_expensive' && priceRange) {
        preferences.dislikedPriceRanges[priceRange] = (preferences.dislikedPriceRanges[priceRange] || 0) + 1;
      } else if (responses.priceFeedback === 'prefer_free') {
        preferences.preferredPriceRange = 'free';
      } else if (responses.priceFeedback === 'prefer_premium') {
        preferences.preferredPriceRange = 'high';
      }
    }

    // Extract keywords from specific dislikes
    if (responses.specificDislikes) {
      const keywords = this.extractKeywords(responses.specificDislikes.toLowerCase());
      preferences.dislikedKeywords = [...new Set([...preferences.dislikedKeywords, ...keywords])];
    }

    // Update location preferences
    if (responses.locationFeedback) {
      if (responses.locationFeedback === 'too_far') {
        preferences.locationPreferences.preferCloser = true;
      } else if (responses.locationFeedback === 'no_transport') {
        preferences.locationPreferences.needsGoodTransport = true;
      }
    }

    // Store general dislikes
    if (responses.reason) {
      preferences.generalDislikes.push({
        reason: responses.reason,
        activityTitle: feedback.activityTitle,
        category: feedback.activityCategory,
        timestamp: feedback.submittedAt
      });
    }

    localStorage.setItem(preferencesKey, JSON.stringify(preferences));
  }

  // Extract keywords from text
  static extractKeywords(text) {
    const commonDislikes = [
      'crowded', 'loud', 'noisy', 'expensive', 'physical', 'demanding', 'outdoor', 'indoor',
      'water', 'heights', 'scary', 'dark', 'long', 'short', 'boring', 'touristy',
      'walking', 'driving', 'public transport', 'early', 'late', 'hot', 'cold',
      'spicy', 'sweet', 'alcohol', 'children', 'adults only', 'group', 'solo'
    ];

    return commonDislikes.filter(keyword => text.includes(keyword));
  }

  // Get user's negative preferences
  static getUserDislikes(userId = null) {
    const currentUser = userId || this.getCurrentUser().id;
    const preferencesKey = `userPreferences_${currentUser}`;
    const preferences = localStorage.getItem(preferencesKey);
    
    if (!preferences) {
      return {
        dislikedCategories: {},
        dislikedPriceRanges: {},
        dislikedKeywords: [],
        dislikedActivities: []
      };
    }

    return JSON.parse(preferences);
  }

  // Get all feedback for analysis
  static getAllFeedback(userId = null) {
    const currentUser = userId || this.getCurrentUser().id;
    const allFeedback = localStorage.getItem('notInterestedFeedback');
    const feedbackData = allFeedback ? JSON.parse(allFeedback) : {};
    
    return feedbackData[currentUser] || [];
  }

  // Check if activity should be filtered out based on user dislikes
  static shouldFilterActivity(activity, userId = null) {
    const dislikes = this.getUserDislikes(userId);
    
    // Check category dislikes (if user has indicated they hate this category multiple times)
    if (activity.category && dislikes.dislikedCategories[activity.category] >= 2) {
      return true;
    }

    // Check price range dislikes
    if (activity.priceRange && dislikes.dislikedPriceRanges[activity.priceRange] >= 2) {
      return true;
    }

    // Check for disliked keywords in title or description
    const searchText = `${activity.title} ${activity.description}`.toLowerCase();
    const hasDislikedKeyword = dislikes.dislikedKeywords.some(keyword => 
      searchText.includes(keyword)
    );
    
    if (hasDislikedKeyword) {
      return true;
    }

    // Check if this specific activity was marked as not interested before
    const previousFeedback = this.getAllFeedback(userId);
    const wasDisliked = previousFeedback.some(feedback => 
      feedback.activityId === activity.id || 
      feedback.activityTitle === activity.title
    );

    return wasDisliked;
  }

  // Filter activities based on user preferences
  static filterRecommendations(activities, userId = null) {
    return activities.filter(activity => !this.shouldFilterActivity(activity, userId));
  }

  // Get learning insights for debugging/admin
  static getLearningInsights(userId = null) {
    const dislikes = this.getUserDislikes(userId);
    const feedback = this.getAllFeedback(userId);
    
    return {
      totalFeedbackCount: feedback.length,
      topDislikedCategories: Object.entries(dislikes.dislikedCategories)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5),
      topDislikedKeywords: dislikes.dislikedKeywords.slice(0, 10),
      preferredPriceRange: dislikes.preferredPriceRange,
      recentFeedback: feedback.slice(-5).reverse()
    };
  }

  // Clear all learning data (for testing or privacy)
  static clearLearningData(userId = null) {
    const currentUser = userId || this.getCurrentUser().id;
    
    // Clear feedback
    const allFeedback = localStorage.getItem('notInterestedFeedback');
    if (allFeedback) {
      const feedbackData = JSON.parse(allFeedback);
      delete feedbackData[currentUser];
      localStorage.setItem('notInterestedFeedback', JSON.stringify(feedbackData));
    }
    
    // Clear preferences
    const preferencesKey = `userPreferences_${currentUser}`;
    localStorage.removeItem(preferencesKey);
    
    return { success: true, message: 'Learning data cleared successfully' };
  }
}

export default PreferenceLearningService;